import { useQueryParam } from "hooks"
import { Card, ErrorFallback } from "materia"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Document, Page } from "react-pdf"
import type {
  DocumentCallback,
  OnLoadProgressArgs,
  PageCallback,
} from "react-pdf/dist/shared/types"
import { modifiers } from "ui-tools"
import { Navigation, Outline, Toolbar } from "./components"
import { BASE_PAGE_WIDTH, PDFContextProvider, usePDFContext } from "./providers"

import "react-pdf/dist/Page/AnnotationLayer.css" // allows to render clickable links
import "react-pdf/dist/Page/TextLayer.css" // allows to render selectable text

import s from "./PDFViewer.module.scss"

type PDFViewerProps = { fileUrl: string; title: string }
export const PDFViewer = ({ fileUrl, title }: PDFViewerProps) => {
  return (
    <PDFContextProvider>
      <Viewer fileUrl={fileUrl} title={title} />
    </PDFContextProvider>
  )
}

const Viewer = ({ fileUrl, title }: PDFViewerProps) => {
  const [loadingPercentage, setLoadingPercentage] = useState<number>(0)
  const [fileError, setFileError] = useState<Error | null>(null)

  const {
    containerRef,
    currentPageNumber,
    zoomFactor,
    setNumberOfPages,
    setPageAspectRatio,
  } = usePDFContext()

  // Page will rerender when either page number or zoom factor changes.
  // This rerender will cause it to blink. We don't want that, so we
  // detect the rerender and display a fake page with old parameters
  // until a new one is rendered.
  const [previousPageNumber, setPreviousPageNumber] =
    useState<number>(currentPageNumber)
  const [previousZoomFactor, setPreviousZoomFactor] =
    useState<number>(zoomFactor)

  const isRerendering =
    previousPageNumber &&
    previousZoomFactor &&
    (previousPageNumber !== currentPageNumber ||
      previousZoomFactor !== zoomFactor)

  const onDocumentLoadProgress = ({ loaded, total }: OnLoadProgressArgs) => {
    const percentage = Math.round((loaded / total) * 100)
    setLoadingPercentage(percentage)
  }

  const onDocumentLoadSuccess = ({ numPages }: DocumentCallback): void => {
    setNumberOfPages(numPages)
  }

  const onDocumentLoadError = (error: Error) => {
    setFileError(error)
  }

  const onPageLoadSuccess = ({ width, height }: PageCallback) => {
    setPageAspectRatio(width / height)
  }

  if (fileError) return <ViewerError error={fileError} />

  return (
    <Document
      className={s.document}
      externalLinkTarget="_blank"
      file={fileUrl}
      key={fileUrl}
      loading={<Progress percentage={loadingPercentage} />}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadProgress={onDocumentLoadProgress}
      onLoadError={onDocumentLoadError}
    >
      <Toolbar fileName={title} />

      <div className={s.layout}>
        <Outline />

        <div className={s.pages} ref={containerRef}>
          {isRerendering ? (
            <Page
              key={`page_${previousPageNumber}_${previousZoomFactor}`}
              pageNumber={previousPageNumber}
              width={BASE_PAGE_WIDTH}
              scale={previousZoomFactor}
              className={modifiers(s, "page", { blurWhileRendering: true })}
            />
          ) : null}

          <Page
            key={`page_${currentPageNumber}_${zoomFactor}`}
            pageNumber={currentPageNumber}
            width={BASE_PAGE_WIDTH}
            scale={zoomFactor}
            className={modifiers(s, "page", {
              hiddenWhileRendering: isRerendering,
            })}
            onLoadSuccess={onPageLoadSuccess}
            onRenderSuccess={() => {
              setPreviousPageNumber(currentPageNumber)
              setPreviousZoomFactor(zoomFactor)
            }}
          />

          <Navigation />
        </div>
      </div>
    </Document>
  )
}

const Progress = ({ percentage }: { percentage: number }) => (
  <div className={s.progressContainer}>
    <progress className={s.progress} value={percentage} max="100" />
  </div>
)

const ViewerError = ({ error }: { error: Error }) => {
  const { t } = useTranslation()

  return (
    <Card>
      <ErrorFallback
        error={error}
        title={t("error.GENERIC")}
        message={t("error.PDF_ERROR")}
      />
    </Card>
  )
}

// [TODO] https://jira.attensi.com/browse/WEB-20655
// Used to toggle between new and old PDF Viewer.
// Remove when PDF Viewer is to be fully released.
export const useIsBetaEnabled = () => {
  const [isBetaEnabled] = useQueryParam("enable_beta_pdf")
  return isBetaEnabled === "true"
}
